import {
  type CurrentOrgData,
  createHubApiClient,
  type HubApiClient,
  HubApiError,
  isGatewayOfflineError,
  notifyUnauthorized,
} from "@lifter/hub-ui/browser-shared";
import { QueryClient, type QueryClientConfig, QueryClientProvider } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useState } from "react";

export type { CurrentOrgData };
export { HubApiError };

interface HubApiProviderProps {
  children: ReactNode;
  baseUrl?: string;
  client?: HubApiClient;
  queryClient?: QueryClient;
}

/**
 * Shared read-query options every domain hook accepts. It lives here, next to
 * `useHubApi`, because it is the common vocabulary the per-domain modules
 * import rather than a resource concern of any one of them.
 */
export interface AgentQueryOptions {
  enabled?: boolean;
  /** When true, bypass cache — data is fetched fresh on every mount. */
  fresh?: boolean;
}

const HubApiContext = createContext<HubApiClient | null>(null);

export function createHubQueryClient(config?: QueryClientConfig): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => shouldRetryQuery(failureCount, error),
        staleTime: 10_000,
      },
      mutations: {
        retry: false,
      },
    },
    ...config,
  });
}

export function HubApiProvider({ children, baseUrl, client, queryClient }: HubApiProviderProps) {
  const [resolvedClient] = useState(
    () => client ?? createHubApiClient({ baseUrl, onUnauthorized: notifyUnauthorized }),
  );
  const [resolvedQueryClient] = useState(() => queryClient ?? createHubQueryClient());

  return (
    <HubApiContext.Provider value={resolvedClient}>
      <QueryClientProvider client={resolvedQueryClient}>{children}</QueryClientProvider>
    </HubApiContext.Provider>
  );
}

export function useHubApi(): HubApiClient {
  const client = useContext(HubApiContext);
  if (!client) {
    throw new Error("HubApiProvider is missing");
  }
  return client;
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isGatewayOfflineError(error)) return false;
  if (error instanceof HubApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 1;
}
